export interface FaqItem {
  question: string;
  answer: string;
}

export interface FaqCategory {
  title: string;
  items: FaqItem[];
}

export const FAQ_CATEGORIES: FaqCategory[] = [
  {
    title: 'About SuiteScanner',
    items: [
      {
        question: 'What is SuiteScanner?',
        answer: 'SuiteScanner is a hotel booking platform built to help you find great hotel prices. We give you access to private rates, compare prices across popular booking sites, and help you book your stay with confidence.\n\nSame room. Lower price. Book like an insider.',
      },
      {
        question: 'How does SuiteScanner find lower hotel prices?',
        answer: "We work with established travel suppliers to access private hotel rates that aren't always available on public booking sites.\n\nWe pass these prices on to you and charge a simple, transparent booking fee. No subscriptions or unnecessary markups.",
      },
      {
        question: 'Is SuiteScanner a real travel booking platform?',
        answer: "Yes! SuiteScanner works with established hotel suppliers to offer accommodation around the world.\n\nYou can search, book, and pay securely through our website. Once your booking is confirmed, we'll send your reservation details by email.",
      },
    ],
  },
  {
    title: 'Prices & Comparisons',
    items: [
      {
        question: 'How does SuiteScanner compare hotel prices?',
        answer: "We believe you deserve to know whether you're getting a great price.\n\nWhen you select a hotel, we compare available prices from other travel sites.\n\nIf we find a lower comparable price elsewhere, we'll show it to you upfront.\n\nNo pretending we're always the cheapest. Just helping you make a better booking decision.",
      },
      {
        question: 'What if another website has a lower price?',
        answer: "We'll show you!\n\nOur goal isn't to convince you that SuiteScanner is always the cheapest. It's to help you find the best available price for your stay.\n\nIf another booking site offers a lower comparable price, we'll help you discover it.",
      },
      {
        question: "Are SuiteScanner's prices really cheaper?",
        answer: "Often, yes! Our access to private hotel rates allows us to offer prices that can be significantly lower than public booking sites.\n\nSavings vary by hotel, destination, travel dates, and room type. We'll show you available comparisons so you can see the difference yourself.",
      },
      {
        question: 'Why might the price change before checkout?',
        answer: "Hotel prices and availability can change in real time.\n\nWe verify your selected rate before checkout. If the price changes, we'll show you the updated amount before you pay.\n\nYou'll never be charged a different amount without your approval.",
      },
      {
        question: 'Do SuiteScanner prices include taxes and property fees?',
        answer: "Our displayed prices include applicable hotel taxes and mandatory charges collected online.\n\nSome hotels may collect additional charges at the property, such as resort fees or city taxes. When disclosed by the hotel or supplier, we'll show these separately before you book.\n\nYou'll always see your total online payment before checkout.",
      },
    ],
  },
  {
    title: 'Booking & Confirmation',
    items: [
      {
        question: 'Do I book directly with SuiteScanner?',
        answer: "Yes! You can search, select, and book your hotel directly through SuiteScanner.\n\nWe work with trusted travel suppliers to secure your reservation with the hotel.\n\nOnce your booking is confirmed, you'll receive your reservation details by email.",
      },
      {
        question: 'When will I receive my booking confirmation?',
        answer: "Most bookings are confirmed shortly after payment.\n\nOnce your hotel confirms the reservation, we'll email you your booking details.\n\nIf confirmation takes longer than expected, we'll keep you updated.",
      },
      {
        question: 'What happens if my booking cannot be confirmed?',
        answer: "If your payment succeeds but your hotel booking cannot be confirmed, we'll help resolve the issue or arrange a refund for the unsuccessful booking.\n\nYou won't be left wondering what happened to your reservation.",
      },
      {
        question: 'Can I book a hotel for someone else?',
        answer: 'Yes! You can book a hotel for a friend, family member, or colleague.\n\nJust make sure the guest details entered during checkout match the person who will be checking in.',
      },
    ],
  },
  {
    title: 'Payments & Fees',
    items: [
      {
        question: 'What does SuiteScanner charge?',
        answer: "Our booking fees are set in USD, based on the total hotel reservation value:\n\nUnder US$1,000: US$39\nUS$1,000 through US$2,000: US$69\nOver US$2,000: US$99\n\nAt checkout, you'll see the converted total and an option to pay in your local currency where available. The room prices you see already include card payment processing costs. Amounts payable at the property are shown separately before you book. Membership has no subscription fee.",
      },
      {
        question: 'Are there any hidden charges?',
        answer: 'Your checkout shows the room price (which already includes card payment processing costs), the SuiteScanner booking fee and the total payable online. Amounts payable directly at the property are shown separately before you pay.',
      },
      {
        question: 'Can I avoid payment processing fees?',
        answer: "Card payment processing costs are included in the room price you see, so there is no separate charge at checkout.\n\nWe're working on supporting ACH and bank transfers, which will let us lower this cost.",
      },
      {
        question: 'Is my payment secure?',
        answer: "We use secure payment processing partners to handle your transactions.\n\nYour payment information is processed through secure payment systems, and you'll receive confirmation of your payment and booking.",
      },
    ],
  },
  {
    title: 'Cancellations & Refunds',
    items: [
      {
        question: 'Can I cancel or change my booking?',
        answer: "Every hotel and room has its own cancellation and modification policy.\n\nSome bookings offer free cancellation, while others are non-refundable.\n\nWe'll clearly show the applicable cancellation policy before you book.\n\nNeed help with an existing reservation? Contact our support team.",
      },
      {
        question: 'Will I receive a refund if I cancel my booking?',
        answer: "Your refund depends on the cancellation policy of the room you booked.\n\nIf your reservation includes free cancellation and you cancel within the permitted window, you'll receive the applicable refund.\n\nNon-refundable bookings may not qualify for a refund.\n\nWe'll show you the cancellation terms before you confirm your reservation.",
      },
      {
        question: 'How long does a refund take?',
        answer: "Once your refund is approved and processed, the time it takes to reach your account depends on your payment method and bank.\n\nWe'll notify you when your refund has been initiated.",
      },
    ],
  },
];

const byQuestion = new Map(FAQ_CATEGORIES.flatMap((category) => category.items).map((item) => [item.question, item]));

export const HOMEPAGE_FAQS: FaqItem[] = [
  'How does SuiteScanner find lower hotel prices?',
  'Is SuiteScanner a real travel booking platform?',
  'How does SuiteScanner compare hotel prices?',
  'What if another website has a lower price?',
  'What does SuiteScanner charge?',
  'Do SuiteScanner prices include taxes and property fees?',
  'Can I cancel or change my booking?',
  'What happens if my booking cannot be confirmed?',
].map((question) => byQuestion.get(question)).filter((item): item is FaqItem => Boolean(item));
